// client/src/utils/messageCacheUtils.js

import StorageManager, { STORAGE_PRIORITY } from './storageManager.js'

/**
 * Message cache utility for improving chat performance
 * Caches recent messages in localStorage to avoid re-fetching
 */

const CACHE_PREFIX = 'flex_chat_messages_'
const CACHE_EXPIRY = 5 * 60 * 1000 // 5 minutes in milliseconds
const MAX_CACHE_ENTRIES = 10 // Limit number of cached channels

function readCacheEntry(channelId) {
  const cacheKey = `${CACHE_PREFIX}${channelId}`
  const cached = StorageManager.get(cacheKey)

  if (!cached) return null

  const parsed = JSON.parse(cached)

  // Check if cache is expired
  if (Date.now() - parsed.timestamp > CACHE_EXPIRY) {
    StorageManager.remove(cacheKey)
    return null
  }

  return parsed
}

function writeCacheEntry(channelId, cacheData) {
  const cacheKey = `${CACHE_PREFIX}${channelId}`

  try {
    // Use LOW priority for message cache - can be evicted during quota pressure
    const success = StorageManager.set(
      cacheKey,
      JSON.stringify(cacheData),
      STORAGE_PRIORITY.LOW,
    )

    if (success) {
      // Clean up old cache entries to prevent localStorage bloat
      cleanupOldCacheEntries()
    }
  } catch (error) {
    console.warn('Error writing message cache:', error)
  }
}

export function getCachedMessagePage(channelId) {
  try {
    const parsed = readCacheEntry(channelId)

    if (!parsed) return null

    return {
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      nextCursor: parsed.nextCursor ?? null,
    }
  } catch (error) {
    console.warn('Error reading message cache:', error)
    return null
  }
}

export function setCachedMessagePage(
  channelId,
  { messages, nextCursor = null },
) {
  // Don't cache empty message arrays
  if (!messages || messages.length === 0) return

  writeCacheEntry(channelId, {
    messages,
    nextCursor,
    timestamp: Date.now(),
  })
}

/**
 * Get cached messages for a channel
 * @param {string|number} channelId - Channel ID
 * @returns {Array|null} Cached messages or null if not found/expired
 */
export function getCachedMessages(channelId) {
  try {
    return getCachedMessagePage(channelId)?.messages ?? null
  } catch (error) {
    console.warn('Error reading message cache:', error)
    return null
  }
}

/**
 * Cache messages for a channel
 * @param {string|number} channelId - Channel ID
 * @param {Array} messages - Messages to cache
 */
export function setCachedMessages(channelId, messages) {
  setCachedMessagePage(channelId, { messages })
}

/**
 * Clear all cached messages
 */
export function clearMessageCache() {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach((key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        StorageManager.remove(key)
      }
    })
  } catch (error) {
    console.warn('Error clearing message cache:', error)
  }
}

/**
 * Clean up old cache entries to prevent localStorage bloat
 */
function cleanupOldCacheEntries() {
  try {
    const cacheEntries = []

    // Collect all cache entries with timestamps
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const cached = StorageManager.get(key)
          const { timestamp } = JSON.parse(cached)
          cacheEntries.push({ key, timestamp })
        } catch {
          // Remove corrupted cache entries
          StorageManager.remove(key)
        }
      }
    })

    // Sort by timestamp (oldest first)
    cacheEntries.sort((a, b) => a.timestamp - b.timestamp)

    // Remove entries beyond the limit
    if (cacheEntries.length > MAX_CACHE_ENTRIES) {
      const entriesToRemove = cacheEntries.slice(
        0,
        cacheEntries.length - MAX_CACHE_ENTRIES,
      )
      entriesToRemove.forEach((entry) => {
        StorageManager.remove(entry.key)
      })
    }

    // Remove expired entries
    const now = Date.now()
    cacheEntries.forEach((entry) => {
      if (now - entry.timestamp > CACHE_EXPIRY) {
        StorageManager.remove(entry.key)
      }
    })
  } catch (error) {
    console.warn('Error cleaning up message cache:', error)
  }
}

/**
 * Check if messages are cached for a channel
 * @param {string|number} channelId - Channel ID
 * @returns {boolean} True if cached and not expired
 */
export function isCached(channelId) {
  return getCachedMessages(channelId) !== null
}
